import { useState, useCallback } from 'react';
import { createLogger } from '../utils/logger';
import type { Address } from '../types';

const logger = createLogger('useViaCep');

interface ViaCepResponse {
  cep: string;
  logradouro: string;
  complemento: string;
  bairro: string;
  localidade: string;
  uf: string;
  ibge: string;
  gia: string;
  ddd: string;
  siafi: string;
  erro?: boolean;
}

interface UseViaCepReturn {
  loading: boolean;
  error: string | null;
  searchCep: (cep: string) => Promise<Partial<Address> | null>;
}

export function useViaCep(): UseViaCepReturn {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const searchCep = useCallback(
    async (cep: string): Promise<Partial<Address> | null> => {
      const formatCep = (cep: string): string => {
        return cep.replace(/\D/g, '');
      };

      const isValidCep = (cep: string): boolean => {
        const cleanCep = formatCep(cep);
        return cleanCep.length === 8;
      };

      const cleanCep = formatCep(cep);

      if (!isValidCep(cleanCep)) {
        setError('CEP deve ter 8 dígitos');
        return null;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await fetch(
          `https://viacep.com.br/ws/${cleanCep}/json/`
        );

        if (!response.ok) {
          throw new Error(`Erro na requisição: ${response.status}`);
        }

        const data: ViaCepResponse = await response.json();

        if (data.erro) {
          setError('CEP não encontrado');
          return null;
        }

        const addressData: Partial<Address> = {
          zipCode: data.cep.replace(/\D/g, ''),
          street: data.logradouro,
          neighborhood: data.bairro,
          city: data.localidade,
          state: data.uf,
        };

        return addressData;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Erro ao buscar CEP';
        logger.error('Erro ao buscar CEP:', err);
        setError(errorMessage);
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return {
    loading,
    error,
    searchCep,
  };
}
